/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { Observable, of, ReplaySubject } from 'rxjs';
import { Field } from '@src/app/shared/field/Field';
import { PreferredSupplier, Supplier, } from '@src/app/model/supplier';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { FieldSet } from '@src/app/shared/form/field-set/field-set.component';
import { FormEmailComponent } from '@src/app/shared/form/form-email/form-email.component';
import { FormPhoneComponent } from '@src/app/shared/form/form-phone/form-phone.component';
import { FormTextAreaComponent } from '@src/app/shared/form/form-text-area/form-text-area.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { FormUrlComponent } from '@src/app/shared/form/form-url/form-url.component';
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { SupplierService } from '@src/app/modules/supply/supplier.service';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { maxChars, minChars } from '@src/app/shared/validators';
import { ActionGridComponent } from '@src/app/shared/action/action-grid.component';
import { MemoField } from '@src/app/shared/memos/MemoField';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DialogOptions } from '@src/app/shared/dialogs/pick-dialog/pick-dialog.component';
import { ActionColor, IFormAction } from '@src/app/shared/form/form.component';
import { uuid } from '@src/app/model/abstract-object';
import { MyInjector } from '@src/app/app.module';
import { RequestContextService } from '@src/app/modules/crm/request-context.service';
import { FormNavLinkComponent } from '@src/app/shared/form/form-nav-button/form-nav-link.component';
import { SupplierPageNavRoute } from '@src/app/routes/supplier-page-nav';
import { ServiceRequestPageNavRoute } from '@src/app/routes/service-request-page-nav';
import { MergeSupplierAction } from '@src/app/modules/supply/preferred-supplier-page/merge-supplier-action';
import { GridField } from '@src/app/shared/grid/grid-field';
import { PreferredSupplierPageNavRoute } from '@src/app/routes/preferred-supplier-page-nav';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { FormCheckboxComponent } from '@src/app/shared/form/form-checkbox/form-checkbox.component';
import { AgentContactPageComponent } from '@src/app/modules/agent/agent-contact-page/agent-contact-page.component';
import { Person } from '@src/app/model/person';
import { map } from 'rxjs/operators';
import { FormButtonComponent } from '@src/app/shared/form/form-button/form-button.component';
import { AppFormControl } from '@src/app/shared/form/app-form-control';

@Component({
    selector: 'app-preferred-supplier-page',
    templateUrl: './supplier-page.component.html',
    styleUrls: ['./supplier-page.component.scss'],
    imports: [FormPageComponent]
})
export class SupplierPageComponent extends AbstractPageComponent {
    dataSvc = inject(SupplierService);
    dialog = inject(MatDialog);
    dialogRef = inject<MatDialogRef<SupplierPageComponent>>(MatDialogRef, { optional: true });
    dialogOptions = inject<DialogOptions>(MAT_DIALOG_DATA, { optional: true });


    nameField = FormTextComponent.make('Name', 'name', {
        hint: 'The name of the company or sole trader who supplies you a service'
    });
    contactField = FormTextComponent.make('Contact Person', 'contactPerson', {
        hint: 'The name of the person you deal with in the company'
    });
    addressField = FormTextAreaComponent.make('Address', 'address', { formColumn: 1, visible: Field.formOnly });
    phoneField = FormPhoneComponent.make('Telephone', 'phone', { formColumn: 2, hint: 'Your normal contact phone number' });
    emailField = FormEmailComponent.make('Email', 'email', { formColumn: 2, hint: 'Your normal contact email address' });
    urlField = FormUrlComponent.make('Web URL', 'web', { formColumn: 3, });

    emergencyPhoneField = FormPhoneComponent.make('Emergency Number', 'emergencyPhone', {
        formColumn: 2, hint: 'Phone number to use in emergencies', visible: Field.formOnly
    });
    accountsEmailField = FormEmailComponent.make('Email Accounts', 'emailAccounts', {
        formColumn: 2, hint: 'Email address to use for accounts queries', visible: Field.formOnly
    });
    vatField = FormTextComponent.make('VAT', 'VAT', {
        formColumn: 3, validators: [maxChars(12), minChars(8)], visible: Field.formOnly,
        hint: 'If supplier is registered for VAT, you should enter their VAT number here'
    });
    trnField = FormTextComponent.make('Tax Registration Number', 'taxRegistrationNr',
        {
            formColumn: 3, validators: [maxChars(12), minChars(8)], visible: Field.formOnly,
            hint: 'If a supplier is not registered for VAT, you still need to report them on form 46G, so you need their tax registration number. For sole traders, this is often their PPS number'
        });
    dunsField = FormTextComponent.make('DUNS', 'DUNS', {
        formColumn: 3, validators: [maxChars(9)],
        hint: 'A DUNS Number is a unique Dun & Bradstreet identifier that can be used to obtain additional company information, e.g. credit checks etc.',
        visible: Field.formOnly
    });

    codeField = FormTextComponent.make('Code', 'code', {
        formColumn: 4,
        hint: 'A unique code used by te agent to identify a supplier.',
        visible: Field.formOnly
    });

    memoField = new MemoField(this.dataSvc);

    bicField = FormTextComponent.make('BIC', 'BIC', { formColumn: 4, validators: [maxChars(11), minChars(7)], visible: Field.formOnly });
    ibanField = FormTextComponent.make('IBAN', 'IBAN', { formColumn: 4, validators: [maxChars(34), minChars(16)], visible: Field.formOnly });
    bankField = FormTextComponent.make('Bank Account Name', 'bankAccountName', { formColumn: 4, validators: [maxChars(40)], visible: Field.formOnly });
    bankDetailField = FormTextAreaComponent.make('Bank Name & Address', 'bankNameAddress', { formColumn: 5, visible: Field.formOnly });

    requestsLink = FormNavLinkComponent.make('Requests',
        ServiceRequestPageNavRoute, o => ({ supplierId: o?.id,  _sort: 'createdAt' }),
        {
            formColumn: 5, value: 'requests_count', cellOpts: {
                style: 'text-align: center; max-width: 3em',
            }
        }
    );
    openRequestsLink = FormNavLinkComponent.make('Open Requests',
        ServiceRequestPageNavRoute, o => ({ supplierId: o?.id, srStatus: 'open', _sort: 'createdAt' }),
        {
            formColumn: 5, value: 'open_requests_count', cellOpts: {
                style: 'text-align: center; max-width: 3em',
            }
        }
    );

    omcUsage =  new GridField({
        field: { value: 'omcUsage', tabIcon: 'domain', cellOpts: { heading: 'OMC Usage' }, visible: Field.formOnly, readonly: true, sendServer: false},
        rowFactory: () => [
            FormNavLinkComponent.makeDetail('OMC', 'teamId', 'supplierId', PreferredSupplierPageNavRoute, true),
            FormNumberComponent.make('Transactions','txns', {}, {readonly: true}),
            FieldMaker.spacer().override({cellOpts: {width: '75%'}})
        ],
    })    

    contactsGrid = new GridField({
        field: { label: $localize`Contacts`, value: 'contacts', formRow: 2, readonly: true, visible: Field.formOnly, sendServer: false },
        rowFactory: (contact: Person) => [
            FormButtonComponent.makeTextButton('Name', 'fullName', (cell) => {
                this.editContact(contact, cell);
            }, { readonly: true }),
            FormEmailComponent.make('Email', 'email', { readonly: true }),
            FormPhoneComponent.make('Telephone', 'phone', { readonly: true }),
            FieldMaker.spacer()
        ],
    });

    actionGrid = ActionGridComponent.make('Supplier').noAdd();

    sharedField = FormCheckboxComponent.make('Shared', 'isShared', {
        readonly: true, calculateValue: (o: Supplier) => o.omcTeamId ? false : true, formColumn: 3
    });
    omcTeamField = FormTextComponent.make('OMC', 'omcTeamId', { readonly: true, disable: true, formColumn: 3});

    configReady = new ReplaySubject<null>(1);

    config = new FormConfig({
        navRoute: SupplierPageNavRoute,
        docPath: 'omc-professional/suppliers',
        title: $localize`Supplier`,
        help: $localize`Agent supplier list`,
        fieldSet: new FieldSet({
            fields: [
                FieldMaker.id(),
                FieldMaker.rev(),
                this.nameField,
                this.sharedField,
                this.contactField,
                this.addressField,
                this.phoneField,
                this.emailField,
                this.emergencyPhoneField,
                this.accountsEmailField,
                this.omcTeamField,                
                this.urlField,
                this.vatField,
                //this.trnField,
                //this.dunsField,
                this.codeField,
                this.bicField,
                this.ibanField,
                this.bankField,
                FieldMaker.notes({ formColumn: 5, visible: Field.formOnly }),
                //this.bankDetailField,
                this.requestsLink,
                this.openRequestsLink,
                this.contactsGrid,
            ],
            formLayout: [{ cells: [{ width: '20%' }, { width: '20%' }, { width: '20%' }, { width: '20%' }, { width: '20%' }] },
            { cells: [{ colspan: 5 }] },
            { cells: [{ colspan: 5, pageTab: 'yes' }] }],
        }),
        service: this.dataSvc,
        mode: 'list',
        objectFactory: this.objectFactory.bind(this),
        tabFields: [this.actionGrid, this.memoField, this.omcUsage],
        actions: [new NewSupplierRequestAction(), new NewSupplierContactAction(), new MergeSupplierAction()]
    });

    objectFactory(): Observable<PreferredSupplier> {
        const s = new PreferredSupplier();
        if (this.dialogOptions && this.dialogOptions['name']) {
            s.name = this.dialogOptions['name'];
        }
        return of(s);
    }

    editContact(contact: Person, cell: AppFormControl) {
        const popDialogRef = this.dialog.open(AgentContactPageComponent, {
            data: { id: contact.id, forceTeamId: contact.teamId }
        });
        popDialogRef.afterClosed().subscribe(person => {
            if (person) {
                Object.assign(contact, person);
                cell.getRow().refreshFrom(contact);
            }
        });
    }
}
export class NewSupplierRequestAction implements IFormAction {
    name = $localize`Create Request`;
    color: ActionColor = 'primary';
    show = true;
    icon = 'add_circle';
    approvalNeeded = false;
    disabled = false;
    approvalText = "";
    tipText: string;
    teamId: uuid;
    supplier: Supplier;

    /* A supplier private to one OMC settles the tenancy; a shared one (omcTeamId null) has to ask. */
    action(supplier: Supplier) {
        MyInjector.instance.get(RequestContextService)
            .newRequest({ requestType: 'workOrder', supplierId: supplier.id, teamId: supplier.omcTeamId })
            .subscribe();
        return of(this.supplier);
    }

    setup(supplier: Supplier) {
        this.show = MyInjector.instance.get(RequestContextService).canCreate();
        this.supplier = supplier;
    }
}

export class NewSupplierContactAction implements IFormAction {
    name = $localize`Add Contact`;
    color: ActionColor = 'primary';
    show = false;
    icon = 'person_add';
    approvalNeeded = false;
    disabled = false;
    approvalText = '';
    tipText = $localize`Add a contact person for this supplier`;
    supplier: Supplier;

    dialog = MyInjector.instance.get(MatDialog);

    action() {
        const dialogRef = this.dialog.open(AgentContactPageComponent, {
            data: { supplierId: this.supplier.id, forceTeamId: this.supplier.teamId }
        });
        return dialogRef.afterClosed().pipe(map((person: Person) => {
            if (person) {
                if (!this.supplier.contacts) { this.supplier.contacts = []; }
                this.supplier.contacts.push(person);
            }
            return this.supplier;
        }));
    }

    setup(supplier: Supplier) {
        this.supplier = supplier;
        this.show = true;
    }
}
